import React, { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PhotoListModal from "./PhotoListModal";

/**
 * Props
 * - id: string
 * - photo: { file_url?, file?, title?, description?, date? } | null
 * - projectId?: string
 * - onSelect?: (id, photo) => void
 * - onDelete?: (id) => void
 * - disabled?: boolean
 */
export default function PhotoCell({
  id,
  photo,
  onSelect,
  projectId,
  onDelete,
  disabled = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const [modalOpen, setModalOpen] = useState(false);

  const style = { transform: CSS.Transform.toString(transform), transition };

  // 드래그 가능 여부
  const draggable = !disabled && (onSelect || onDelete);

  // 클릭으로 모달 열기 가능 여부
  const selectable = !disabled && !!onSelect;

  const imgSrc = photo?.file_url || photo?.file;

  const openModal = () => {
    if (!selectable) return;
    setModalOpen(true);
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...(draggable ? attributes : {})}
        className="relative w-full h-full flex flex-col border bg-white overflow-hidden print-avoid-break"
      >
        {/* 삭제 버튼 */}
        {photo && onDelete && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // ✅ 클릭이 모달 열기로 안 튀게
              onDelete?.(id);
            }}
            className="no-print absolute top-1 right-1 z-10 bg-red-500 text-white text-xs px-2 py-1 rounded"
          >
            ✕
          </button>
        )}

        {/* ✅ 사진 영역 (클릭하면 모달) */}
        <div
          className={`flex-1 w-full flex items-center justify-center bg-gray-100 overflow-hidden ${
            selectable ? "cursor-pointer hover:bg-gray-200" : ""
          }`}
          onClick={openModal}
          // ✅ 드래그/클릭 충돌 줄이기: 선택 가능할 때 클릭을 우선 먹게
          onPointerDown={(e) => {
            if (selectable) e.stopPropagation();
          }}
        >
          {photo ? (
            <img
              src={imgSrc}
              alt={photo.title || "photo"}
              className="w-full h-full object-contain"
              // ✅ 드래그는 이미지에서만 잡히게 (원래 느낌 유지)
              {...(draggable ? listeners : {})}
            />
          ) : (
            <span className="text-xs text-gray-400">
              {selectable ? "클릭해서 사진 추가" : "빈 칸"}
            </span>
          )}
        </div>

        {/* 설명/날짜 영역 */}
        <div className="p-2 bg-white border-t flex flex-col justify-between">
          {photo ? (
            <>
              <div className="text-xs font-medium mb-1 truncate">
                내용: {photo.description || "내용 없음"}
              </div>
              <div className="text-xs text-gray-500">
                날짜: {photo.date || "날짜 없음"}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-400">내용</div>
          )}
        </div>
      </div>

      {/* 사진 선택 모달 */}
      {onSelect && !disabled && (
        <PhotoListModal
          projectId={projectId}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSelect={(newPhoto) => {
            onSelect?.(id, newPhoto);
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
}
